/**
 * Panel — static panel chrome (title bar + bordered surface body).
 *
 * This is NOT a draggable window — for that, use ToolWindow. Panel is the
 * lightweight, in-flow container for inline UI: a titled box inside another
 * layout, a settings group, a section of a larger window. Built on
 * panelSurface() so it matches the rest of the family.
 */
import React, { type ReactNode } from 'react';
import {
  COLORS,
  SPACE,
  FONT_SIZE,
  FONT_WEIGHT,
  panelSurface,
} from '../theme';

export type PanelVariant = 'surface' | 'raised';

export interface PanelProps {
  title?: ReactNode;
  icon?: ReactNode;
  onClose?: () => void;
  width?: number | string;
  height?: number | string;
  variant?: PanelVariant;
  children?: ReactNode;
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.sm,
  padding: `${SPACE.md}px ${SPACE.lg}px`,
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
};

const titleStyle: React.CSSProperties = {
  flex: 1,
  fontSize: FONT_SIZE.lg,
  fontWeight: FONT_WEIGHT.semibold,
  color: COLORS.textPrimary,
};

const closeButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  padding: 0,
  background: 'transparent',
  border: 'none',
  color: COLORS.textMuted,
  fontSize: FONT_SIZE.lg,
  lineHeight: 1,
  cursor: 'pointer',
};

const bodyStyle: React.CSSProperties = {
  padding: SPACE.lg,
};

export function Panel({
  title,
  icon,
  onClose,
  width,
  height,
  variant = 'surface',
  children,
}: PanelProps): React.ReactElement {
  const containerStyle: React.CSSProperties = {
    ...panelSurface(),
    ...(variant === 'raised' ? { background: COLORS.surfaceRaised } : {}),
    display: 'flex',
    flexDirection: 'column',
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
  };

  const hasHeader = title != null || icon != null || onClose != null;

  return (
    <div style={containerStyle}>
      {hasHeader && (
        <div style={headerStyle}>
          {icon != null && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
          {title != null && <span style={titleStyle}>{title}</span>}
          {onClose != null && (
            <button type="button" style={closeButtonStyle} onClick={onClose} title="Close" aria-label="Close">
              ×
            </button>
          )}
        </div>
      )}
      <div style={{ ...bodyStyle, ...(height != null ? { flex: 1, overflow: 'auto' } : {}) }}>{children}</div>
    </div>
  );
}
